import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NotificationItem } from "../models/Notification";
import { getNotifications } from "../services/Notification";

interface NotificationContextProps {
  showAlert: boolean;
  setShowAlert: React.Dispatch<React.SetStateAction<boolean>>;
  notificationItem: NotificationItem[] | null;
  setNotificationItem: (notificationItem: NotificationItem[] | null) => void;
  fetchNotificationItem: (options: {
    is_read: boolean;
    page: number;
    groupId?: number[];
  }) => void;
  totalItems: number;
  unreadCount: number;
  fetchUnreadCount: (options?: { groupId?: number[] }) => void;
}

export const NotificationItemsContext =
  createContext<NotificationContextProps | null>(null);

export const useNotificationItemsContext = () =>
  useContext(NotificationItemsContext)!;

const NotificationItemsProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [notificationItem, setNotificationItem] = useState<
    NotificationItem[] | null
  >([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const fetchNotificationItem = useCallback(
    async (options: {
      page?: number;
      is_read: boolean;
      camera_ids?: number[];
    }) => {
      try {
        const data = await getNotifications({
          page: options.page ?? 1,
          is_read: options.is_read,
          camera_ids: options.camera_ids,
        });

        setNotificationItem(data.results); // only current page's data
        setTotalItems(data.count); // total count of matching items
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setNotificationItem([]);
        setTotalItems(0);
      }
    },
    []
  );

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getNotifications({
        is_read: false,
        page: 1, // Required if your API defaults to something else
      });

      // console.log("counts from here", data.count);
      setUnreadCount(data.count);
    } catch (error) {
      console.error("Failed to fetch unread count", error);
      setUnreadCount(0);
    }
  }, []);

  // Timer to periodically fetch notifications and unread counts
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval); // Clear the interval on component unmount
  }, [fetchUnreadCount]);

  const values = useMemo(
    () => ({
      showAlert,
      setShowAlert,
      notificationItem,
      fetchNotificationItem,
      setNotificationItem,
      totalItems,
      unreadCount,
      fetchUnreadCount,
    }),
    [
      notificationItem,
      fetchNotificationItem,
      showAlert,
      setShowAlert,
      totalItems,
      unreadCount,
      fetchUnreadCount,
    ]
  );
  return (
    <NotificationItemsContext.Provider value={values}>
      {children}
    </NotificationItemsContext.Provider>
  );
};

export default NotificationItemsProvider;
